/**
 * Utility functions for exporting analytics data to CSV/Excel format
 */

interface ExportColumn {
  header: string;
  key: string;
  formatter?: (value: any) => string;
}

const formatCurrency = (value: number) => `£${value.toFixed(2)}`;
const formatPercent = (value: number) => `${value.toFixed(1)}%`;

/**
 * Convert array of objects to CSV string
 */
function arrayToCSV<T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn[]
): string {
  if (data.length === 0) return '';
  
  const headers = columns.map(col => col.header).join(',');
  const rows = data.map(item =>
    columns.map(col => {
      const value = item[col.key];
      const formatted = col.formatter ? col.formatter(value) : value;
      // Escape quotes and wrap in quotes if contains comma or newline
      const str = String(formatted ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(',')
  );
  
  return [headers, ...rows].join('\n');
}

/**
 * Trigger download of CSV file
 */
function downloadCSV(content: string, filename: string) {
  const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get current date string for filenames
 */
function getDateString() {
  return new Date().toISOString().split('T')[0];
}

// ==================== GA4 TRAFFIC EXPORTS ====================

interface GATrafficSummary {
  users: number;
  sessions: number;
  pageviews: number;
  newUsers: number;
  returningUsers: number;
  engagementRate: number;
  avgSessionDuration: number;
  conversions: number;
  revenue: number;
  conversionRate: number;
}

interface GATopPage {
  pageTitle: string;
  pagePath: string;
  pageViews: number;
}

interface GASource {
  sourceMedium: string;
  sessions: number;
  conversions?: number;
  revenue?: number;
  purchases?: number;
}

interface GADevice {
  deviceCategory: string;
  sessions: number;
}

interface GACountry {
  country: string;
  sessions: number;
}

interface GADailyTrend {
  date: string;
  users: number;
  sessions: number;
  pageviews: number;
}

interface GALandingPage {
  landingPage: string;
  sessions: number;
  conversions: number;
  revenue: number;
}

interface GAFunnel {
  sessions: number;
  viewItem: number;
  addToCart: number;
  beginCheckout: number;
  purchase: number;
  addToCartRate: number;
  checkoutStartRate: number;
  checkoutToPurchaseRate: number;
}

export function exportGATrafficSummary(summary: GATrafficSummary, range: string) {
  const data = [
    { metric: 'Total Users', value: summary.users },
    { metric: 'Total Sessions', value: summary.sessions },
    { metric: 'Page Views', value: summary.pageviews },
    { metric: 'New Users', value: summary.newUsers },
    { metric: 'Returning Users', value: summary.returningUsers },
    { metric: 'Engagement Rate', value: formatPercent(summary.engagementRate) },
    { metric: 'Avg Session Duration (sec)', value: summary.avgSessionDuration.toFixed(0) },
    { metric: 'Conversions', value: summary.conversions },
    { metric: 'Revenue', value: formatCurrency(summary.revenue) },
    { metric: 'Conversion Rate', value: formatPercent(summary.conversionRate) },
  ];
  
  const csv = arrayToCSV(data, [
    { header: 'Metric', key: 'metric' },
    { header: 'Value', key: 'value' },
  ]);
  
  downloadCSV(csv, `ga-traffic-summary-${range}-${getDateString()}.csv`);
}

export function exportGATopPages(pages: GATopPage[], range: string) {
  const csv = arrayToCSV(pages, [
    { header: 'Page Title', key: 'pageTitle' },
    { header: 'Page Path', key: 'pagePath' },
    { header: 'Page Views', key: 'pageViews' },
  ]);
  
  downloadCSV(csv, `ga-top-pages-${range}-${getDateString()}.csv`);
}

export function exportGASources(sources: GASource[], range: string) {
  const hasRevenue = sources.some(s => s.revenue !== undefined);
  
  const columns: ExportColumn[] = [
    { header: 'Source / Medium', key: 'sourceMedium' },
    { header: 'Sessions', key: 'sessions' },
  ];
  
  if (hasRevenue) {
    columns.push(
      { header: 'Conversions', key: 'conversions' },
      { header: 'Revenue', key: 'revenue', formatter: (v) => formatCurrency(v || 0) },
      { header: 'Purchases', key: 'purchases' }
    );
  }
  
  const csv = arrayToCSV(sources, columns);
  downloadCSV(csv, `ga-traffic-sources-${range}-${getDateString()}.csv`);
}

export function exportGADevices(devices: GADevice[], range: string) {
  const csv = arrayToCSV(devices, [
    { header: 'Device Category', key: 'deviceCategory' },
    { header: 'Sessions', key: 'sessions' },
  ]);
  
  downloadCSV(csv, `ga-devices-${range}-${getDateString()}.csv`);
}

export function exportGACountries(countries: GACountry[], range: string) {
  const csv = arrayToCSV(countries, [
    { header: 'Country', key: 'country' },
    { header: 'Sessions', key: 'sessions' },
  ]);
  
  downloadCSV(csv, `ga-countries-${range}-${getDateString()}.csv`);
}

export function exportGADailyTrends(trends: GADailyTrend[], range: string) {
  const csv = arrayToCSV(trends, [
    { header: 'Date', key: 'date' },
    { header: 'Users', key: 'users' },
    { header: 'Sessions', key: 'sessions' },
    { header: 'Page Views', key: 'pageviews' },
  ]);
  
  downloadCSV(csv, `ga-daily-trends-${range}-${getDateString()}.csv`);
}

export function exportGALandingPages(pages: GALandingPage[], range: string) {
  const csv = arrayToCSV(pages, [
    { header: 'Landing Page', key: 'landingPage' },
    { header: 'Sessions', key: 'sessions' },
    { header: 'Conversions', key: 'conversions' },
    { header: 'Revenue', key: 'revenue', formatter: (v) => formatCurrency(v || 0) },
  ]);
  
  downloadCSV(csv, `ga-landing-pages-${range}-${getDateString()}.csv`);
}

export function exportGAFunnel(funnel: GAFunnel, range: string) {
  const data = [
    { stage: 'Sessions', count: funnel.sessions, rate: '100%' },
    { stage: 'View Item', count: funnel.viewItem, rate: formatPercent((funnel.viewItem / funnel.sessions) * 100 || 0) },
    { stage: 'Add to Cart', count: funnel.addToCart, rate: formatPercent(funnel.addToCartRate) },
    { stage: 'Begin Checkout', count: funnel.beginCheckout, rate: formatPercent(funnel.checkoutStartRate) },
    { stage: 'Purchase', count: funnel.purchase, rate: formatPercent(funnel.checkoutToPurchaseRate) },
  ];
  
  const csv = arrayToCSV(data, [
    { header: 'Funnel Stage', key: 'stage' },
    { header: 'Count', key: 'count' },
    { header: 'Conversion Rate', key: 'rate' },
  ]);
  
  downloadCSV(csv, `ga-conversion-funnel-${range}-${getDateString()}.csv`);
}

// ==================== STORE ORDER EXPORTS ====================

interface ProductStat {
  name: string;
  units: number;
  revenue: number;
  lastOrdered: string;
}

interface CustomerStat {
  email: string;
  name: string;
  spend: number;
  orders: number;
  lastOrder: string;
}

interface RecentOrder {
  id: string;
  date: string;
  customer: string;
  total: number;
  status: string;
  fulfilled: boolean;
}

interface StoreMetrics {
  revenue: { today: number; sevenDays: number; thirtyDays: number };
  orders: { today: number; sevenDays: number; thirtyDays: number };
  avgOrderValue: number;
  unfulfilledCount: number;
  avgFulfilmentHours: number;
}

export function exportStoreMetrics(metrics: StoreMetrics) {
  const data = [
    { metric: 'Revenue Today', value: formatCurrency(metrics.revenue.today) },
    { metric: 'Revenue (7 Days)', value: formatCurrency(metrics.revenue.sevenDays) },
    { metric: 'Revenue (30 Days)', value: formatCurrency(metrics.revenue.thirtyDays) },
    { metric: 'Orders Today', value: metrics.orders.today },
    { metric: 'Orders (7 Days)', value: metrics.orders.sevenDays },
    { metric: 'Orders (30 Days)', value: metrics.orders.thirtyDays },
    { metric: 'Avg Order Value', value: formatCurrency(metrics.avgOrderValue) },
    { metric: 'Unfulfilled Orders', value: metrics.unfulfilledCount },
    { metric: 'Avg Fulfilment Time (hours)', value: metrics.avgFulfilmentHours.toFixed(1) },
  ];
  
  const csv = arrayToCSV(data, [
    { header: 'Metric', key: 'metric' },
    { header: 'Value', key: 'value' },
  ]);
  
  downloadCSV(csv, `store-metrics-${getDateString()}.csv`);
}

export function exportTopProducts(products: ProductStat[]) {
  const csv = arrayToCSV(products, [
    { header: 'Product Name', key: 'name' },
    { header: 'Units Sold', key: 'units' },
    { header: 'Revenue', key: 'revenue', formatter: (v) => formatCurrency(v) },
    { header: 'Last Ordered', key: 'lastOrdered' },
  ]);
  
  downloadCSV(csv, `store-top-products-${getDateString()}.csv`);
}

export function exportTopCustomers(customers: CustomerStat[]) {
  const csv = arrayToCSV(customers, [
    { header: 'Name', key: 'name' },
    { header: 'Email', key: 'email' },
    { header: 'Total Spend', key: 'spend', formatter: (v) => formatCurrency(v) },
    { header: 'Orders', key: 'orders' },
    { header: 'Last Order', key: 'lastOrder' },
  ]);
  
  downloadCSV(csv, `store-top-customers-${getDateString()}.csv`);
}

export function exportRecentOrders(orders: RecentOrder[]) {
  const csv = arrayToCSV(orders, [
    { header: 'Order ID', key: 'id' },
    { header: 'Date', key: 'date' },
    { header: 'Customer', key: 'customer' },
    { header: 'Total', key: 'total', formatter: (v) => formatCurrency(v) },
    { header: 'Status', key: 'status' },
    { header: 'Fulfilled', key: 'fulfilled', formatter: (v) => v ? 'Yes' : 'No' },
  ]);
  
  downloadCSV(csv, `store-recent-orders-${getDateString()}.csv`);
}

// ==================== FULL REPORT EXPORTS ====================

interface FullGAData {
  summary: GATrafficSummary;
  topPages: GATopPage[];
  sourceRevenue: GASource[];
  deviceBreakdown: GADevice[];
  countryBreakdown: GACountry[];
  dailyTrend: GADailyTrend[];
  landingPages?: GALandingPage[];
  funnel?: GAFunnel;
}

interface FullStoreData {
  metrics: StoreMetrics;
  topProducts: ProductStat[];
  topCustomers: CustomerStat[];
  recentTrends: RecentOrder[];
}

export function exportFullGAReport(data: FullGAData, range: string) {
  let csv = '=== GA4 TRAFFIC REPORT ===\n';
  csv += `Report Period: ${range}\n`;
  csv += `Generated: ${new Date().toLocaleString('en-GB')}\n\n`;
  
  // Summary
  csv += '--- SUMMARY ---\n';
  csv += `Metric,Value\n`;
  csv += `Total Users,${data.summary.users}\n`;
  csv += `Total Sessions,${data.summary.sessions}\n`;
  csv += `Page Views,${data.summary.pageviews}\n`;
  csv += `New Users,${data.summary.newUsers}\n`;
  csv += `Returning Users,${data.summary.returningUsers}\n`;
  csv += `Engagement Rate,${formatPercent(data.summary.engagementRate)}\n`;
  csv += `Avg Session Duration,${data.summary.avgSessionDuration.toFixed(0)}s\n`;
  csv += `Conversions,${data.summary.conversions}\n`;
  csv += `Revenue,${formatCurrency(data.summary.revenue)}\n`;
  csv += `Conversion Rate,${formatPercent(data.summary.conversionRate)}\n\n`;
  
  // Funnel
  if (data.funnel) {
    csv += '--- CONVERSION FUNNEL ---\n';
    csv += `Stage,Count,Rate\n`;
    csv += `Sessions,${data.funnel.sessions},100%\n`;
    csv += `Add to Cart,${data.funnel.addToCart},${formatPercent(data.funnel.addToCartRate)}\n`;
    csv += `Begin Checkout,${data.funnel.beginCheckout},${formatPercent(data.funnel.checkoutStartRate)}\n`;
    csv += `Purchase,${data.funnel.purchase},${formatPercent(data.funnel.checkoutToPurchaseRate)}\n\n`;
  }
  
  // Top Pages
  csv += '--- TOP PAGES ---\n';
  csv += `Page Title,Page Path,Views\n`;
  data.topPages.forEach(p => {
    csv += `"${p.pageTitle}","${p.pagePath}",${p.pageViews}\n`;
  });
  csv += '\n';
  
  // Traffic Sources
  csv += '--- TRAFFIC SOURCES ---\n';
  csv += `Source/Medium,Sessions,Conversions,Revenue\n`;
  data.sourceRevenue.forEach(s => {
    csv += `"${s.sourceMedium}",${s.sessions},${s.conversions || 0},${formatCurrency(s.revenue || 0)}\n`;
  });
  csv += '\n';
  
  // Devices
  csv += '--- DEVICES ---\n';
  csv += `Device,Sessions\n`;
  data.deviceBreakdown.forEach(d => {
    csv += `${d.deviceCategory},${d.sessions}\n`;
  });
  csv += '\n';
  
  // Countries
  csv += '--- COUNTRIES ---\n';
  csv += `Country,Sessions\n`;
  data.countryBreakdown.forEach(c => {
    csv += `"${c.country}",${c.sessions}\n`;
  });
  csv += '\n';
  
  // Daily Trends
  csv += '--- DAILY TRENDS ---\n';
  csv += `Date,Users,Sessions,Page Views\n`;
  data.dailyTrend.forEach(t => {
    csv += `${t.date},${t.users},${t.sessions},${t.pageviews}\n`;
  });
  
  downloadCSV(csv, `ga-full-report-${range}-${getDateString()}.csv`);
}

export function exportFullStoreReport(data: FullStoreData) {
  let csv = '=== STORE ORDERS REPORT ===\n';
  csv += `Generated: ${new Date().toLocaleString('en-GB')}\n\n`;
  
  // Metrics
  csv += '--- REVENUE SUMMARY ---\n';
  csv += `Period,Revenue,Orders\n`;
  csv += `Today,${formatCurrency(data.metrics.revenue.today)},${data.metrics.orders.today}\n`;
  csv += `7 Days,${formatCurrency(data.metrics.revenue.sevenDays)},${data.metrics.orders.sevenDays}\n`;
  csv += `30 Days,${formatCurrency(data.metrics.revenue.thirtyDays)},${data.metrics.orders.thirtyDays}\n\n`;
  
  csv += '--- KEY METRICS ---\n';
  csv += `Metric,Value\n`;
  csv += `Avg Order Value,${formatCurrency(data.metrics.avgOrderValue)}\n`;
  csv += `Unfulfilled Orders,${data.metrics.unfulfilledCount}\n`;
  csv += `Avg Fulfilment Time,${data.metrics.avgFulfilmentHours.toFixed(1)} hours\n\n`;
  
  // Top Products
  csv += '--- TOP PRODUCTS ---\n';
  csv += `Product,Units,Revenue,Last Ordered\n`;
  data.topProducts.forEach(p => {
    csv += `"${p.name}",${p.units},${formatCurrency(p.revenue)},"${p.lastOrdered}"\n`;
  });
  csv += '\n';
  
  // Top Customers
  csv += '--- TOP CUSTOMERS ---\n';
  csv += `Name,Email,Spend,Orders,Last Order\n`;
  data.topCustomers.forEach(c => {
    csv += `"${c.name}","${c.email}",${formatCurrency(c.spend)},${c.orders},"${c.lastOrder}"\n`;
  });
  csv += '\n';
  
  // Recent Orders
  csv += '--- RECENT ORDERS ---\n';
  csv += `Order ID,Date,Customer,Total,Status,Fulfilled\n`;
  data.recentTrends.forEach(o => {
    csv += `"${o.id}","${o.date}","${o.customer}",${formatCurrency(o.total)},"${o.status}",${o.fulfilled ? 'Yes' : 'No'}\n`;
  });
  
  downloadCSV(csv, `store-full-report-${getDateString()}.csv`);
}
